import { byNumericId } from './id-sort.ts';
import type { AddonFilterConfig } from './schema.ts';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function extractConfigLiteral(text: string): string | null {
  const keyword = 'window.addonFilterConfig';
  const idx = text.indexOf(keyword);
  if (idx === -1) return null;

  let i = idx + keyword.length;
  while (i < text.length && /\s/.test(text[i])) i++;
  if (i >= text.length || text[i] !== '=') return null;
  i++;
  while (i < text.length && /\s/.test(text[i])) i++;
  if (i >= text.length || text[i] !== '{') return null;

  const start = i;
  let depth = 1;
  i++;
  while (i < text.length && depth > 0) {
    const c = text[i];
    if (c === '"' || c === "'") {
      const quote = c;
      i++;
      while (i < text.length && text[i] !== quote) {
        if (text[i] === '\\') i++;
        i++;
      }
      i++;
    } else if (c === '{') {
      depth++;
      i++;
    } else if (c === '}') {
      depth--;
      if (depth === 0) {
        i++;
        break;
      }
      i++;
    } else {
      i++;
    }
  }

  if (depth !== 0) return null;
  return text.slice(start, i);
}

export function normalizeJsObjectLiteral(str: string): string {
  let result = '';
  let i = 0;
  while (i < str.length) {
    const c = str[i];
    if (c === '"' || c === "'") {
      const quote = c;
      result += '"';
      i++;
      while (i < str.length && str[i] !== quote) {
        if (str[i] === '\\') {
          const next = str[i + 1];
          if (next === "'") {
            result += "'";
            i += 2;
          } else {
            result += str[i] + next;
            i += 2;
          }
        } else {
          if (str[i] === '"') result += '\\';
          result += str[i];
          i++;
        }
      }
      result += '"';
      i++;
    } else if (/[a-zA-Z_$0-9]/.test(c)) {
      let j = i;
      while (j < str.length && /[a-zA-Z0-9_$]/.test(str[j])) j++;
      const id = str.slice(i, j);
      let k = j;
      while (k < str.length && /\s/.test(str[k])) k++;
      if (k < str.length && str[k] === ':') {
        result += '"' + id + '"';
      } else {
        result += id;
      }
      i = j;
    } else {
      result += c;
      i++;
    }
  }
  result = result.replace(/,\s*([\]\}])/g, '$1');
  return result;
}

export function parseConfigFromText(text: string): AddonFilterConfig | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  let jsonText = trimmed;
  const extracted = extractConfigLiteral(trimmed);
  if (extracted) {
    jsonText = extracted;
  } else {
    const m = trimmed.match(/window\.addonFilterConfig\s*=\s*([\s\S]+);?\s*$/);
    if (m) jsonText = m[1].trim().replace(/;\s*$/, '');
  }

  try {
    const obj = JSON.parse(jsonText);
    if (!isPlainObject(obj)) return null;
    return {
      activeTicketGroupId: String(obj.activeTicketGroupId || ''),
      activeTicketAddonMap: isPlainObject(obj.activeTicketAddonMap) ? obj.activeTicketAddonMap : {},
      defaultOpenCategories: Array.isArray(obj.defaultOpenCategories) ? obj.defaultOpenCategories : [],
      addonCategoryLabels: isPlainObject(obj.addonCategoryLabels) ? obj.addonCategoryLabels : {},
      addonCategoryById: isPlainObject(obj.addonCategoryById) ? obj.addonCategoryById : {},
    };
  } catch {
    const normalized = normalizeJsObjectLiteral(jsonText);
    if (normalized) {
      try {
        const obj = JSON.parse(normalized);
        if (!isPlainObject(obj)) return null;
        return {
          activeTicketGroupId: String(obj.activeTicketGroupId || ''),
          activeTicketAddonMap: isPlainObject(obj.activeTicketAddonMap) ? obj.activeTicketAddonMap : {},
          defaultOpenCategories: Array.isArray(obj.defaultOpenCategories) ? obj.defaultOpenCategories : [],
          addonCategoryLabels: isPlainObject(obj.addonCategoryLabels) ? obj.addonCategoryLabels : {},
          addonCategoryById: isPlainObject(obj.addonCategoryById) ? obj.addonCategoryById : {},
        };
      } catch { /* ignore */ }
    }
    return null;
  }
}

export function applyParsedConfig(
  parsed: AddonFilterConfig,
  state: {
    activeTicketGroupId: string;
    activeTicketAddonMap: Record<string, string[]>;
    defaultOpenCategories: string[];
    addonCategoryById: Record<string, string>;
    categories: { key: string; label: string; openDefault: boolean }[];
    selectedTicketId: string | null;
    selectedAddonIds: Set<string>;
    selectedAddonTableIds: Set<string>;
  }
): void {
  state.activeTicketGroupId = parsed.activeTicketGroupId || '';
  state.activeTicketAddonMap = parsed.activeTicketAddonMap || {};
  state.defaultOpenCategories = parsed.defaultOpenCategories || [];
  state.addonCategoryById = parsed.addonCategoryById || {};

  const labelKeys = Object.keys(parsed.addonCategoryLabels || {});
  const usedKeys = new Set(Object.values(parsed.addonCategoryById || {}));
  const allKeys = new Set([...labelKeys, ...usedKeys]);

  state.categories = [];
  for (const key of allKeys) {
    const label = parsed.addonCategoryLabels?.[key] || key;
    const openDefault = (parsed.defaultOpenCategories || []).includes(key);
    state.categories.push({ key, label, openDefault });
  }

  const normalizedMap: Record<string, string[]> = {};
  for (const ticketId of Object.keys(state.activeTicketAddonMap).sort(byNumericId)) {
    normalizedMap[String(ticketId)] = idSetSorted(state.activeTicketAddonMap[ticketId]);
  }
  state.activeTicketAddonMap = normalizedMap;

  const normalizedCategory: Record<string, string> = {};
  for (const addonId of Object.keys(state.addonCategoryById).sort(byNumericId)) {
    normalizedCategory[String(addonId)] = state.addonCategoryById[addonId];
  }
  state.addonCategoryById = normalizedCategory;

  state.selectedTicketId = Object.keys(state.activeTicketAddonMap)[0] || null;
  state.selectedAddonIds = new Set();
  state.selectedAddonTableIds = new Set();
}

function idSetSorted(arr: string[] | unknown): string[] {
  if (!Array.isArray(arr)) return [];
  return [...new Set(arr.map(String))].sort(byNumericId);
}
